import { memo, useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Api } from "../../services/interceptor";
import { QUERY_KEY } from "../../services/queryEndPoint";
import { resolvePublicImageUrl } from "../../helper/helper";
import { Images } from "../../utils/imageConstants";
import type { ITeamsSquad } from "../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import toast from "react-hot-toast";
import "./AssetSetup.scss";

// ── Constants ──────────────────────────────────────────────────────────────────

const PRESIGNED_URLS_ENDPOINT = "/uploads/presigned-urls";
const TICKER_ASSETS_ENDPOINT = "/tournament/ticker/assets";

const ACCEPTED_MIME = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB

// ── Types ──────────────────────────────────────────────────────────────────────

type TickerAssetType = "HEAD_SHOT" | "PROFILE_IMAGE";

interface TickerAssetPayload {
  playerId: string;
  teamSnapShotId: string;
  assetType: TickerAssetType;
  assetUrl: string;
}

interface SaveTickerAssetsRequest {
  tournamentId: string;
  assets: TickerAssetPayload[];
}

async function saveTickerAssets(payload: SaveTickerAssetsRequest): Promise<void> {
  await Api.post(TICKER_ASSETS_ENDPOINT, payload);
}

interface AssetSetupState {
  leagueId: string;
  leagueName: string;
  tournamentId: string;
  tournamentName: string;
}

interface TeamSnapshot {
  teamSnapshotId: string;
  teamName: string;
  imageUrl?: string;
  description?: string;
  teamCode?: string;
}

interface DivisionResponse {
  divisionId?: string;
  divisionName?: string;
  childDivisions?: DivisionResponse[];
  teams?: TeamSnapshot[];
}

interface TournamentDivisionsResponse {
  tournamentId?: string;
  divisionResponseList?: DivisionResponse[];
  canEdit?: boolean;
  canDelete?: boolean;
}

type UploadStatus = "idle" | "uploading" | "success" | "error";
type ImageType = "headShot" | "profile";

interface LightboxState {
  src: string;
  label: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function collectTeams(divisions: DivisionResponse[]): TeamSnapshot[] {
  const teams: TeamSnapshot[] = [];
  const seen = new Set<string>();
  const traverse = (list: DivisionResponse[]) => {
    for (const div of list) {
      for (const team of div.teams ?? []) {
        if (!seen.has(team.teamSnapshotId)) {
          seen.add(team.teamSnapshotId);
          teams.push(team);
        }
      }
      if (div.childDivisions?.length) traverse(div.childDivisions);
    }
  };
  traverse(divisions);
  return teams;
}

function dedupeByPlayerId(players: ITeamsSquad[]): ITeamsSquad[] {
  const seen = new Set<string>();
  const out: ITeamsSquad[] = [];
  for (const p of players) {
    const id = p.playerId?.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(p);
  }
  return out;
}

function getInitials(first?: string, last?: string): string {
  const f = (first ?? "").trim().charAt(0).toUpperCase();
  const l = (last ?? "").trim().charAt(0).toUpperCase();
  return f && l ? `${f}${l}` : f || l || "–";
}

function validateFile(file: File): string | null {
  if (!ACCEPTED_MIME.includes(file.type)) {
    return `Unsupported file type "${file.type || file.name.split(".").pop()}". Please upload a JPG, PNG, or WEBP image.`;
  }
  if (file.size > MAX_FILE_BYTES) {
    return `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum allowed size is 5 MB.`;
  }
  return null;
}

// ── Image lightbox ─────────────────────────────────────────────────────────────

const ImageLightbox = memo(({
  state,
  onClose,
}: {
  state: LightboxState;
  onClose: () => void;
}) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div
      className="asset-setup__lightbox-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={state.label}
    >
      <div
        className="asset-setup__lightbox-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="asset-setup__lightbox-close"
          onClick={onClose}
          aria-label="Close preview"
        >
          ✕
        </button>
        <p className="asset-setup__lightbox-label">{state.label}</p>
        <img
          src={state.src}
          alt={state.label}
          className="asset-setup__lightbox-img"
        />
      </div>
    </div>
  );
});

// ── Image upload cell ──────────────────────────────────────────────────────────

interface ImageUploadCellProps {
  playerId: string;
  teamSnapshotId: string;
  tournamentId: string;
  type: ImageType;
  existingUrl?: string | null;
  playerName: string;
  onOpenPreview: (src: string, label: string) => void;
}

const IMAGE_TYPE_TO_ASSET_TYPE: Record<ImageType, TickerAssetType> = {
  headShot: "HEAD_SHOT",
  profile: "PROFILE_IMAGE",
};

const ImageUploadCell = memo(({
  playerId,
  teamSnapshotId,
  tournamentId,
  type,
  existingUrl,
  playerName,
  onOpenPreview,
}: ImageUploadCellProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    existingUrl ? resolvePublicImageUrl(existingUrl) : null
  );
  const [status, setStatus] = useState<UploadStatus>("idle");
  const objectUrlRef = useRef<string | null>(null);

  // Clean up any object URL on unmount
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Reset so the same file can be re-selected after an error
      e.target.value = "";

      const validationError = validateFile(file);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      // Revoke previous object URL if any
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }

      const localUrl = URL.createObjectURL(file);
      objectUrlRef.current = localUrl;
      setPreviewUrl(localUrl);
      setStatus("uploading");

      try {
        const clientImageId = crypto.randomUUID();

        const { data } = await Api.post(PRESIGNED_URLS_ENDPOINT, {
          files: [
            {
              clientImageId,
              fileName: file.name,
              contentType: file.type,
              fileSize: file.size,
            },
          ],
          bucketName: "TICKER_ASSET_BUCKET",
        });

        const upload = (data.uploads as Array<{ clientImageId: string; uploadUrl: string; uploadId: string; s3Key: string }>)
          .find((u) => u.clientImageId === clientImageId);

        if (!upload?.uploadUrl) throw new Error("No upload URL returned");

        await fetch(upload.uploadUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type },
        });

        await saveTickerAssets({
          tournamentId,
          assets: [
            {
              playerId,
              teamSnapShotId: teamSnapshotId,
              assetType: IMAGE_TYPE_TO_ASSET_TYPE[type],
              assetUrl: upload.s3Key,
            },
          ],
        });

        setStatus("success");
      } catch {
        setStatus("error");
        toast.error("Upload failed. Please try again.");
      }
    },
    [playerId, teamSnapshotId, tournamentId, type]
  );

  const label = `${playerName} — ${type === "headShot" ? "Head Shot" : "Profile Image"}`;

  const handleThumbnailClick = useCallback(() => {
    if (previewUrl) onOpenPreview(previewUrl, label);
  }, [previewUrl, label, onOpenPreview]);

  return (
    <div className="asset-setup__img-cell">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        className="asset-setup__img-input"
        onChange={handleFileChange}
        aria-label={`Upload ${label}`}
      />

      {previewUrl ? (
        <div className="asset-setup__img-preview-wrap">
          <button
            type="button"
            className="asset-setup__img-thumbnail-btn"
            onClick={handleThumbnailClick}
            title="Click to enlarge"
          >
            <img
              src={previewUrl}
              alt={label}
              className="asset-setup__img-thumbnail"
            />
          </button>
          <div className="asset-setup__img-actions">
            {status === "uploading" && (
              <span className="asset-setup__img-status asset-setup__img-status--uploading">
                <span className="asset-setup__spinner" />
                Uploading…
              </span>
            )}
            {status === "success" && (
              <span className="asset-setup__img-status asset-setup__img-status--success">
                ✓ Saved
              </span>
            )}
            {status === "error" && (
              <span className="asset-setup__img-status asset-setup__img-status--error">
                ✗ Failed
              </span>
            )}
            <button
              type="button"
              className="asset-setup__img-change-btn"
              onClick={() => inputRef.current?.click()}
              disabled={status === "uploading"}
            >
              Change
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="asset-setup__img-upload-btn"
          onClick={() => inputRef.current?.click()}
          title={`Upload ${type === "headShot" ? "head shot" : "profile image"}`}
        >
          <svg
            className="asset-setup__img-upload-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <span>Upload</span>
          <span className="asset-setup__img-hint">JPG, PNG, WEBP · max 5 MB</span>
        </button>
      )}
    </div>
  );
});

// ── Team list item ─────────────────────────────────────────────────────────────

const TeamListItem = memo(({
  team,
  isActive,
  onClick,
}: {
  team: TeamSnapshot;
  isActive: boolean;
  onClick: () => void;
}) => {
  const src = resolvePublicImageUrl(team.imageUrl ?? null) || Images.card_info_image;
  return (
    <button
      type="button"
      className={`asset-setup__team-item${isActive ? " asset-setup__team-item--active" : ""}`}
      onClick={onClick}
    >
      <div className="asset-setup__team-item-avatar">
        <img
          src={src}
          alt={team.teamName}
          onError={(e) => {
            if (e.currentTarget.src !== Images.card_info_image)
              e.currentTarget.src = Images.card_info_image;
          }}
        />
      </div>
      <div className="asset-setup__team-item-info">
        <span className="asset-setup__team-item-name">{team.teamName}</span>
        {team.teamCode && (
          <span className="asset-setup__team-item-code">{team.teamCode}</span>
        )}
      </div>
    </button>
  );
});

// ── Skeleton components ────────────────────────────────────────────────────────

const TeamListSkeleton = () => (
  <>
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="asset-setup__team-item asset-setup__team-item--skeleton">
        <div className="asset-setup__team-item-avatar asset-setup__skeleton-block" />
        <div className="asset-setup__team-item-info">
          <div className="asset-setup__skeleton-block asset-setup__skeleton-name" />
          <div className="asset-setup__skeleton-block asset-setup__skeleton-code" />
        </div>
      </div>
    ))}
  </>
);

const PlayersTableSkeleton = () => (
  <tbody>
    {Array.from({ length: 6 }).map((_, i) => (
      <tr key={i} className="asset-setup__table-row--skeleton">
        <td>
          <div className="asset-setup__player-cell">
            <div className="asset-setup__player-avatar asset-setup__skeleton-block" />
            <div className="asset-setup__skeleton-block asset-setup__skeleton-name" />
          </div>
        </td>
        <td><div className="asset-setup__skeleton-block asset-setup__skeleton-img-cell" /></td>
        <td><div className="asset-setup__skeleton-block asset-setup__skeleton-img-cell" /></td>
      </tr>
    ))}
  </tbody>
);

// ── Main component ─────────────────────────────────────────────────────────────

const AssetSetup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as AssetSetupState | null;
  const tournamentId = state?.tournamentId;

  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);

  const { data: divisionsData, isLoading: teamsLoading } =
    useQuery<TournamentDivisionsResponse>({
      queryKey: ["getDivisions", tournamentId],
      queryFn: async () => {
        const res = await Api.get(`/getDivisions/${tournamentId}`);
        return res.data;
      },
      enabled: !!tournamentId,
    });

  const teams = useMemo(
    () => collectTeams(divisionsData?.divisionResponseList ?? []),
    [divisionsData]
  );

  useEffect(() => {
    if (!selectedTeamId && teams.length > 0) {
      setSelectedTeamId(teams[0].teamSnapshotId);
    }
  }, [teams, selectedTeamId]);

  const { data: rawPlayers = [], isLoading: playersLoading } = useQuery<ITeamsSquad[]>({
    queryKey: [QUERY_KEY.getAllPlayers, selectedTeamId],
    queryFn: async () => {
      const res = await Api.get(`${QUERY_KEY.getAllPlayers}/${selectedTeamId}`);
      return (res.data ?? []) as ITeamsSquad[];
    },
    enabled: !!selectedTeamId,
  });

  const players = useMemo(() => dedupeByPlayerId(rawPlayers), [rawPlayers]);
  const selectedTeam = teams.find((t) => t.teamSnapshotId === selectedTeamId);

  const openPreview = useCallback((src: string, label: string) => {
    setLightbox({ src, label });
  }, []);

  const closeLightbox = useCallback(() => setLightbox(null), []);

  return (
    <div className="asset-setup">
      {lightbox && (
        <ImageLightbox state={lightbox} onClose={closeLightbox} />
      )}

      <div className="asset-setup__header">
        <button className="asset-setup__back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1 className="asset-setup__title">Asset Setup</h1>
      </div>

      <div className="asset-setup__selection-info">
        <div className="asset-setup__info-card">
          <span className="asset-setup__info-label">League</span>
          <span className="asset-setup__info-value">{state?.leagueName ?? "—"}</span>
        </div>
        <div className="asset-setup__info-card">
          <span className="asset-setup__info-label">Tournament</span>
          <span className="asset-setup__info-value">{state?.tournamentName ?? "—"}</span>
        </div>
      </div>

      <div className="asset-setup__body">
        {/* ── Left: team list ── */}
        <aside className="asset-setup__sidebar">
          <div className="asset-setup__sidebar-header">
            <span className="asset-setup__sidebar-title">Teams</span>
            {!teamsLoading && (
              <span className="asset-setup__section-count">{teams.length}</span>
            )}
          </div>
          <div className="asset-setup__team-list">
            {teamsLoading ? (
              <TeamListSkeleton />
            ) : teams.length === 0 ? (
              <p className="asset-setup__sidebar-empty">No teams found.</p>
            ) : (
              teams.map((team) => (
                <TeamListItem
                  key={team.teamSnapshotId}
                  team={team}
                  isActive={team.teamSnapshotId === selectedTeamId}
                  onClick={() => setSelectedTeamId(team.teamSnapshotId)}
                />
              ))
            )}
          </div>
        </aside>

        {/* ── Right: players table ── */}
        <div className="asset-setup__content">
          <div className="asset-setup__content-header">
            <div className="asset-setup__content-title-row">
              <h2 className="asset-setup__content-title">
                {selectedTeam?.teamName ?? "Players"}
              </h2>
              {!playersLoading && selectedTeam && (
                <span className="asset-setup__section-count">{players.length}</span>
              )}
            </div>
            {selectedTeam?.teamCode && (
              <span className="asset-setup__content-subtitle">{selectedTeam.teamCode}</span>
            )}
          </div>

          <div className="asset-setup__table-wrap">
            <table className="asset-setup__players-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Head Shot Image</th>
                  <th>Profile Image</th>
                </tr>
              </thead>

              {playersLoading ? (
                <PlayersTableSkeleton />
              ) : players.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan={3}>
                      <div className="asset-setup__table-empty">
                        No players found for this team.
                      </div>
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody>
                  {players.map((player) => {
                    const fullName =
                      [player.playerFirstName, player.playerMiddleName, player.playerLastName]
                        .filter(Boolean)
                        .join(" ") || "—";
                    const avatarSrc = resolvePublicImageUrl(player.imageUri) || null;
                    const initials = getInitials(player.playerFirstName, player.playerLastName);

                    return (
                      <tr key={player.playerId}>
                        <td>
                          <div className="asset-setup__player-cell">
                            <div className="asset-setup__player-avatar">
                              {avatarSrc ? (
                                <img
                                  src={avatarSrc}
                                  alt={fullName}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    const next = e.currentTarget
                                      .nextElementSibling as HTMLElement | null;
                                    if (next) next.style.display = "flex";
                                  }}
                                />
                              ) : null}
                              <span
                                className="asset-setup__player-avatar-initials"
                                style={{ display: avatarSrc ? "none" : "flex" }}
                              >
                                {initials}
                              </span>
                            </div>
                            <span className="asset-setup__player-name">{fullName}</span>
                          </div>
                        </td>
                        <td>
                          <ImageUploadCell
                            playerId={player.playerId}
                            teamSnapshotId={selectedTeamId!}
                            tournamentId={tournamentId!}
                            type="headShot"
                            existingUrl={null}
                            playerName={fullName}
                            onOpenPreview={openPreview}
                          />
                        </td>
                        <td>
                          <ImageUploadCell
                            playerId={player.playerId}
                            teamSnapshotId={selectedTeamId!}
                            tournamentId={tournamentId!}
                            type="profile"
                            existingUrl={null}
                            playerName={fullName}
                            onOpenPreview={openPreview}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetSetup;
